<script setup>
// core
import {computed, ref} from "vue";
import {useRouter} from "vue-router";

// components
import HeaderMain from "@/components/Recurring/HeaderMain.vue";
import BaseCopyButton from "@/components/ui/BaseCopyButton.vue";
import PreloaderBase from "@/components/Recurring/PreloaderBase.vue";

// constants
import {
    DEFAULT_SIP_TRUNK_NAME,
    SIP_SERVER,
    ZOIPER_DOWNLOAD_URL,
    ZOIPER_GUIDE_URL
} from "@/components/Telephony/constants";

// assets
import telephonySvg from "@/assets/icons/messengers/viber_telephony.svg";

const PASSWORD_MASK = '••••••••••••'

const props = defineProps({
    status: {
        type: String,
        required: true
    },
    number: {
        type: String,
        default: ''
    },
    extension: {
        type: Object,
        default: null
    },
    loading: {
        type: Boolean,
        default: false
    }
})

const router = useRouter()

const isFirst = computed(() => props.status === 'first')
const isSuccess = computed(() => props.status !== 'exists')

const isPasswordVisible = ref(false)

const trunkName = computed(() => props.extension?.name || DEFAULT_SIP_TRUNK_NAME)

const goToSettings = () => router.push({name: 'TelephonySettings'})
const goToCabinet = () => router.push({name: 'Integration'})
</script>

<template>
    <div class="activation">
        <HeaderMain>{{ $t('telephony.activation_result.mainheader') }}</HeaderMain>

        <div class="activation__block" :class="{ 'is-compact': !isFirst }">
            <div class="activation__head">
                <div class="activation__logo">
                    <img :src="telephonySvg" alt="Viber telephony"/>
                </div>

                <div class="activation__start">
                    <p class="activation__status" :class="isSuccess ? 'is-success' : 'is-exists'">
                        <i :class="['fa', isSuccess ? 'fa-check-circle-o' : 'fa-info-circle']"/>
                        {{
                            isSuccess
                                ? $t('telephony.activation_result.success_title')
                                : $t('telephony.activation_result.exists_title')
                        }}
                    </p>

                    <p v-if="isFirst" class="activation__text">
                        <i18n-t keypath="telephony.activation_result.first_text" tag="span">
                            <template #number>
                                <b class="activation__number">{{ number }}</b>
                            </template>
                            <template #name>
                                <b class="activation__number">{{ trunkName }}</b>
                            </template>
                        </i18n-t>

                        <span class="activation__line">
                            <b>{{ $t('telephony.activation_result.first_workplace') }}</b>
                        </span>

                        <i18n-t
                            keypath="telephony.activation_result.first_managers"
                            tag="span"
                            class="activation__line"
                        >
                            <template #link>
                                <a class="activation__link" href="#" @click.prevent="goToSettings">
                                    {{ $t('telephony.activation_result.settings_link') }}
                                </a>
                            </template>
                        </i18n-t>
                    </p>

                    <p v-else class="activation__text">
                        <i18n-t
                            :keypath="isSuccess
                                ? 'telephony.activation_result.next_text'
                                : 'telephony.activation_result.exists_text'"
                            tag="span"
                        >
                            <template #number>
                                <b class="activation__number">{{ number }}</b>
                            </template>
                            <template #link>
                                <a class="activation__link" href="#" @click.prevent="goToSettings">
                                    {{ $t('telephony.activation_result.settings_link') }}
                                </a>
                            </template>
                        </i18n-t>

                        <span v-if="isSuccess" class="activation__line">
                            {{ $t('telephony.activation_result.next_note') }}
                        </span>
                    </p>

                    <div v-if="!isFirst" class="activation__actions">
                        <button class="button --thin" type="button" @click="goToSettings">
                            {{ $t('telephony.activation_result.to_settings') }}
                        </button>
                        <button class="button --thin --ghost" type="button" @click="goToCabinet">
                            {{ $t('telephony.activation_result.to_cabinet') }}
                        </button>
                    </div>
                </div>
            </div>

            <template v-if="isFirst">
                <section class="setup">
                    <p class="setup__title">{{ $t('telephony.activation_result.setup_title') }}</p>
                    <p class="setup__text">{{ $t('telephony.activation_result.setup_text') }}</p>

                    <div class="setup__grid">
                        <div class="sip">
                            <p class="sip__title">
                                <i class="bi bi-headset"></i>
                                {{ $t('telephony.activation_result.credentials_title') }}
                                {{ trunkName }}
                            </p>

                            <div v-if="loading" class="sip__loading">
                                <PreloaderBase/>
                            </div>

                            <div v-else class="sip__rows">
                                <div class="sip__row">
                                    <span class="sip__row-label">{{ $t('telephony.sip.server') }}</span>
                                    <span class="sip__row-value">{{ SIP_SERVER }}</span>
                                    <BaseCopyButton
                                        :value="SIP_SERVER"
                                        :title="$t('telephony.sip.copy_server')"
                                        toast="telephony.sip.copied"
                                    />
                                </div>

                                <div class="sip__row">
                                    <span class="sip__row-label">{{ $t('telephony.sip.login_label') }}</span>
                                    <span class="sip__row-value">{{ extension?.credentials?.username }}</span>
                                    <BaseCopyButton
                                        :value="extension?.credentials?.username"
                                        :title="$t('telephony.sip.copy_login')"
                                        toast="telephony.sip.copied"
                                    />
                                </div>

                                <div class="sip__row">
                                    <span class="sip__row-label">{{ $t('telephony.sip.password_label') }}</span>
                                    <span
                                        class="sip__row-value sip__pass"
                                        :class="{ 'is-visible': isPasswordVisible }"
                                    >{{ isPasswordVisible ? extension?.credentials?.password : PASSWORD_MASK }}</span>
                                    <button
                                        type="button"
                                        class="inside_block --sm"
                                        :title="$t(isPasswordVisible ? 'telephony.sip.hide_password' : 'telephony.sip.show_password')"
                                        @click="isPasswordVisible = !isPasswordVisible"
                                    >
                                        <i class="icon fas" :class="isPasswordVisible ? 'fa-eye-slash' : 'fa-eye'"></i>
                                    </button>
                                    <BaseCopyButton
                                        :value="extension?.credentials?.password"
                                        :title="$t('telephony.sip.copy_password')"
                                        toast="telephony.sip.copied"
                                    />
                                </div>
                            </div>
                        </div>

                        <div class="apps">
                            <p class="apps__title">
                                <i class="bi bi-telephone-outbound"></i>
                                {{ $t('telephony.activation_result.app_title') }}
                            </p>

                            <i18n-t class="apps__text" keypath="telephony.activation_result.app_text" tag="p">
                                <template #zoiper><b>Zoiper</b></template>
                                <template #linphone><b>Linphone</b></template>
                                <template #xlite><b>X-Lite</b></template>
                                <template #bria><b>Bria Solo</b></template>
                            </i18n-t>

                            <div class="apps__actions">
                                <a class="apps__btn" :href="ZOIPER_DOWNLOAD_URL" target="_blank" rel="noopener">
                                    <i class="bi bi-download"></i>
                                    {{ $t('telephony.activation_result.download') }}
                                </a>

                                <a class="apps__link" :href="ZOIPER_GUIDE_URL" target="_blank" rel="noopener">
                                    {{ $t('telephony.activation_result.how_to') }}
                                    <i class="bi bi-arrow-right"></i>
                                </a>
                            </div>
                        </div>
                    </div>

                    <p class="setup__note">{{ $t('telephony.activation_result.note') }}</p>
                </section>

                <div class="activation__actions">
                    <button class="button --thin" type="button" @click="goToSettings">
                        {{ $t('telephony.activation_result.to_settings') }}
                    </button>
                    <button class="button --thin --ghost" type="button" @click="goToCabinet">
                        {{ $t('telephony.activation_result.to_cabinet') }}
                    </button>
                </div>
            </template>
        </div>
    </div>
</template>

<style scoped lang="scss">
.activation {
    &__block {
        margin-top: 30px;
        padding: 23px 48px;

        width: 100%;
        border-radius: 11px;
        background: var(--block-color);
        display: flex;
        flex-direction: column;
        gap: 22px;

        box-sizing: border-box;

        &.is-compact .activation__head {
            align-items: center;
        }
    }

    &__head {
        display: flex;
        align-items: flex-start;
        gap: 52px;
    }

    &__start {
        flex: 1;
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 12px;
        align-items: flex-start;
    }

    &__logo {
        flex: 0 0 auto;
        width: 140px;
        height: 140px;
        border-radius: 24px;
        background: var(--general-color);
        display: flex;
        align-items: center;
        justify-content: center;

        img {
            width: 96px;
            height: 96px;
        }
    }

    &__status {
        display: flex;
        align-items: center;
        gap: 12px;

        font-family: 'Gilroy', sans-serif;
        font-size: 24px;
        font-weight: 700;
        line-height: 140%;
        text-transform: none;

        &.is-success {
            color: var(--success-color);
        }

        &.is-exists {
            color: var(--warning-color);
        }

        i {
            font-size: 28px;
        }
    }

    &__number {
        font-weight: 700;
        color: var(--text-color);
        letter-spacing: 0.05rem;
    }

    &__link {
        color: var(--general-color) !important;
        text-decoration: none;

        &:hover {
            text-decoration: underline;
            text-underline-offset: 3px;
        }
    }

    &__line {
        display: block;
    }

    &__text {
        color: var(--text-third-color);
        font-family: 'Gilroy', sans-serif;
        font-size: 16px;
        font-weight: 500;
        line-height: 170%;
        padding: 0 20px 0 0;
        text-transform: none;

        b {
            font-weight: 700;
            color: var(--text-color);
        }
    }

    &__actions {
        display: flex;
        align-items: center;
        gap: 10px;
        flex-wrap: wrap;
    }
}

.button {
    width: auto;
    min-width: 0;
    max-width: none;
    height: 37px;
    padding: 0 20px;
    white-space: nowrap;

    background: var(--general-color);
    border: 1px solid var(--general-color);
    color: #ffffff;
    user-select: none;
    border-radius: 11px;
    font-family: Gilroy;
    font-weight: 600;
    font-size: 13px;
    line-height: 17px;
    letter-spacing: 0.02em;
    text-align: center;
    text-transform: uppercase;
    cursor: pointer;
    transition-property: background, color;
    transition-duration: 0.3s;

    &:hover {
        background: transparent;
        color: var(--general-color);
    }

    &.--ghost {
        background: transparent;
        color: var(--general-color);

        &:hover {
            background: var(--general-color);
            color: #ffffff;
        }
    }
}

.setup {
    width: 100%;
    box-sizing: border-box;

    padding-top: 20px;
    border-top: 1px solid #cdcaf3;

    &__title {
        margin: 0 0 8px;
        font-family: 'Gilroy', sans-serif;
        font-size: 16px;
        font-weight: 700;
        text-transform: uppercase;
        color: var(--text-color);
    }

    &__text {
        margin: 0;
        font-family: 'Gilroy', sans-serif;
        font-size: 15px;
        font-weight: 500;
        line-height: 160%;
        color: var(--text-third-color);
    }

    &__grid {
        display: flex;
        align-items: stretch;
        gap: 16px;
        margin-top: 14px;
    }

    &__note {
        margin: 16px 0 0;
        font-family: 'Gilroy', sans-serif;
        font-size: 16px;
        font-weight: 500;
        line-height: 170%;
        color: var(--text-third-color);
    }
}

%card {
    flex: 1 1 0;
    min-width: 0;
    padding: 16px 20px;
    border-radius: 7px;
    border: 1px solid #cdcaf3;
    background-color: var(--block-second-color);
    box-sizing: border-box;
}

%card-title {
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0 0 14px;

    font-family: 'Gilroy', sans-serif;
    font-size: 16px;
    font-weight: 700;
    text-transform: uppercase;
    color: var(--text-color);

    i {
        font-size: 18px;
        line-height: 1;
    }
}

.sip {
    @extend %card;

    &__title {
        @extend %card-title;
    }

    &__rows {
        display: flex;
        flex-direction: column;
        gap: 10px;
    }

    &__loading {
        position: relative;
        min-height: 106px;
    }

    &__row {
        display: flex;
        align-items: center;
        gap: 10px;
        min-width: 0;

        &-label {
            flex: 0 0 96px;
            display: flex;

            text-transform: uppercase;
            font-family: 'Gilroy', sans-serif;
            font-size: 12px;
            font-weight: 500;
            letter-spacing: 0.02em;
            color: var(--border-primary-color);

            &::after {
                content: ":";
            }
        }

        &-value {
            min-width: 0;
            letter-spacing: 0.05rem;
            font-family: 'Gilroy', sans-serif;
            font-size: 15px;
            font-weight: 500;
            color: var(--text-color);
            white-space: nowrap;
            overflow-x: auto;
            scrollbar-width: none;
            -ms-overflow-style: none;

            &::-webkit-scrollbar {
                display: none;
            }
        }
    }

    &__pass {
        letter-spacing: 0.18em;

        &.is-visible {
            letter-spacing: 0.05rem;
        }
    }
}

.apps {
    @extend %card;

    display: flex;
    flex-direction: column;

    &__title {
        @extend %card-title;
    }

    &__text {
        margin: 0;
        font-family: 'Gilroy', sans-serif;
        font-size: 15px;
        font-weight: 500;
        line-height: 160%;
        color: var(--text-third-color);

        b {
            font-weight: 700;
            color: var(--text-color);
        }
    }

    &__actions {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 14px;
        margin-top: auto;
        padding-top: 14px;
    }

    &__btn {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        height: 34px;
        padding: 0 16px;

        border: 1px solid var(--general-color);
        border-radius: 9px;
        background: transparent;

        font-family: 'Gilroy', sans-serif;
        font-size: 13px;
        font-weight: 600;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        color: var(--general-color) !important;
        text-decoration: none;
        transition: 0.25s;

        &:hover {
            background: var(--general-color);
            color: #ffffff !important;
        }
    }

    &__link {
        margin-left: auto;

        display: inline-flex;
        align-items: center;
        gap: 8px;

        font-family: 'Gilroy', sans-serif;
        font-size: 13px;
        font-weight: 500;
        letter-spacing: 0.02em;
        text-transform: uppercase;
        line-height: 24px;
        // app.css paints every link with `color: var(--general-color) !important`
        color: var(--border-primary-color) !important;
        text-decoration: none;
    }
}

.inside_block {
    position: relative;
    min-width: 27px;
    width: 27px;
    height: 27px;
    max-width: none;
    flex: 0 0 auto;
    padding: 0;

    display: flex;
    align-items: center;
    justify-content: center;

    border-radius: 7px;
    border: 1px solid var(--general-color);
    background-color: #ffffff;
    cursor: pointer;
    transition: 0.25s;

    .icon {
        font-size: 13px;
        line-height: 1;
        fill: var(--general-color);
        color: var(--general-color);
        transition: 0.25s;
    }

    svg {
        width: 13px;
        height: 13px;
    }

    &:hover {
        background-color: var(--general-color);

        .icon {
            fill: #ffffff;
            color: #ffffff;
        }
    }
}

@media screen and (max-width: 900px) {
    .setup__grid {
        flex-direction: column;
    }
}

@media screen and (max-width: 680px) {
    .activation {
        &__block {
            padding: 15px;
        }

        &__head {
            flex-direction: column;
            gap: 16px;
        }

        &__status {
            font-size: 18px;

            i {
                font-size: 22px;
            }
        }

        &__text {
            font-size: 14px;
            line-height: 150%;
        }

        &__start {
            padding: 0;
            width: 100%;
        }

        &__actions {
            width: 100%;

            .button {
                width: 100%;
            }
        }
    }

    .sip__row {
        flex-wrap: wrap;

        &-label {
            flex: 0 0 100%;
        }
    }

    .apps {
        &__btn {
            width: 100%;
            justify-content: center;
        }

        &__link {
            margin-left: 0;
        }
    }
}
</style>
